import { useCallback, useEffect, useRef, useState } from "react";
import { CANDID_TITLE } from "@tellescope/constants";
import { useTellescopeQueryClient } from "@tellescope/tanstack-query";
import type { AnswerForType, Enduser } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { TellescopeFormFieldBaseProps, TellescopeFieldResponse } from "../../types";
import { getLatestInsuranceAnswer } from "../../utils";

export type CandidEligibilityPhase = "idle" | "loading" | "error" | "result" | "cached";

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const extractCandidResult = (
  responseData: unknown
): { status: string; benefits?: object; planMetadata?: object; errors?: unknown[] } | undefined => {
  if (!isObject(responseData)) return undefined;
  return {
    status: typeof responseData.status === "string" ? responseData.status : "UNKNOWN",
    benefits: isObject(responseData.benefits) ? responseData.benefits : undefined,
    planMetadata: isObject(responseData.planMetadata) ? responseData.planMetadata : undefined,
    errors: Array.isArray(responseData.errors) && responseData.errors.length ? responseData.errors : undefined,
  };
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return undefined;
};

interface UseCandidEligibilityFieldArgs {
  value: AnswerForType["Candid Eligibility"];
  onChange: (newVal: AnswerForType["Candid Eligibility"]) => void;
  options: TellescopeFormFieldBaseProps<"Candid Eligibility">["options"];
  enduserId: string;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  priorResponses: TellescopeFieldResponse[];
  sessionType?: "enduser" | "user";
}

export const useCandidEligibilityField = ({
  value,
  onChange,
  options,
  enduserId,
  priorResponses,
  sessionType = "enduser",
}: UseCandidEligibilityFieldArgs) => {
  const { fetchQuery } = useTellescopeQueryClient();

  const insuranceAnswer = getLatestInsuranceAnswer(priorResponses);
  const payerId = insuranceAnswer?.payerId;
  const memberId = insuranceAnswer?.memberId;
  const payerName = insuranceAnswer?.payerName;

  const isCached = !!(value?.status && value.payerId === payerId);

  const [phase, setPhase] = useState<CandidEligibilityPhase>("idle");
  const [result, setResult] = useState<AnswerForType["Candid Eligibility"]>();
  const [responseErrors, setResponseErrors] = useState<unknown[]>();
  const [errorMessage, setErrorMessage] = useState<string>();

  // A billed check must not be issued twice concurrently; fetchQuery always reaches the network, so
  // this guard — not the query cache — is what makes a double click safe.
  const isRunningRef = useRef(false);

  const latestRef = useRef({ onChange, options, enduserId, payerId, memberId, payerName, fetchQuery });
  latestRef.current = { onChange, options, enduserId, payerId, memberId, payerName, fetchQuery };

  const runCheck = useCallback(async () => {
    if (isRunningRef.current) return;

    const { onChange, options, enduserId, payerId, memberId, payerName, fetchQuery } = latestRef.current;
    isRunningRef.current = true;
    setErrorMessage(undefined);
    setResponseErrors(undefined);
    setPhase("loading");

    try {
      const response = await fetchQuery({
        model: "integrations",
        method: "proxy_read",
        request: {
          id: enduserId,
          integration: CANDID_TITLE,
          type: "candid-eligibility",
          query: JSON.stringify({
            serviceCode: options?.candidServiceCode,
            npi: options?.candidNPI,
            payerId,
            memberId,
            payerName,
          }),
        },
      });

      const data = extractCandidResult(response?.data);
      setResponseErrors(data?.errors);
      setPhase("result");
      if (!data) return;

      const reported = {
        payerId,
        status: data.status,
        benefits: data.benefits,
        planMetadata: data.planMetadata,
      };
      setResult(reported);
      onChange(reported);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
      setPhase("error");
    } finally {
      isRunningRef.current = false;
    }
  }, []);

  const triggerCheck = useCallback(() => void runCheck(), [runCheck]);

  // The patient-facing check runs on its own; the guard ref (not the dependency list) is what keeps a
  // StrictMode double-invoke or a re-render from issuing a second billed call.
  const hasAutoRunRef = useRef(false);
  const shouldAutoRun = sessionType === "enduser" && !isCached;
  useEffect(() => {
    if (!shouldAutoRun || hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    void runCheck();
  }, [runCheck, shouldAutoRun]);

  return {
    phase: isCached ? "cached" : phase,
    displayStatus: isCached ? value?.status : result?.status,
    payerName,
    payerId,
    memberId,
    errorMessage,
    responseErrors,
    isChecking: phase === "loading",
    triggerCheck,
  };
};
